class HeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.swiper || this.pending) return;

    this.pending = true;
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    if (window.Swiper) {
      this.init();
    } else {
      window.addEventListener('load', () => this.init(), {
        once: true,
        signal: this.lifecycle.signal,
      });
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.lifecycle?.abort();
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.pending = false;
  }

  init() {
    if (this.swiper || !window.Swiper) return;

    const slides = [...this.querySelectorAll('.hero-carousel__slide')];
    if (slides.length < 2) {
      this.pending = false;
      this.classList.add('is-ready');
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
    const delay = Number(this.dataset.delay) || 7000;
    const counter = this.querySelector('.hero-carousel__counter');

    this.listeners = new AbortController();
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      speed: 450,
      rewind: true,
      navigation: {
        prevEl: this.querySelector('.hero-carousel__prev'),
        nextEl: this.querySelector('.hero-carousel__next'),
      },
      autoplay: autoplay
        ? {
            delay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    });

    const update = () => {
      const active = this.swiper.realIndex ?? this.swiper.activeIndex;
      counter.textContent = `${active + 1} / ${slides.length}`;
      slides.forEach((slide, index) => {
        const hidden = index !== active;
        slide.setAttribute('aria-hidden', String(hidden));
        if ('inert' in slide) slide.inert = hidden;
      });
    };

    this.swiper.on('slideChange', update);
    this.addEventListener('focusin', () => this.swiper.autoplay?.stop(), {
      signal: this.listeners.signal,
    });
    this.addEventListener(
      'focusout',
      (event) => {
        if (autoplay && !this.contains(event.relatedTarget)) this.swiper.autoplay?.start();
      },
      { signal: this.listeners.signal },
    );
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.swiper.autoplay?.stop();
        } else if (autoplay) {
          this.swiper.autoplay?.start();
        }
      },
      { signal: this.listeners.signal },
    );

    update();
    this.classList.add('is-ready');
    this.pending = false;
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
